import React from 'react';
import SkillTree from './SkillTree';

import Store from 'store';
import Locale from 'data/locale';

import './CharacterPane.scss';
import { Color } from 'common';
import NumberInput from 'ui/number';
import { profileMaxAttributes, profileUpdateAttributes } from 'logic/stateReducers';
import { selectStats } from 'logic/stats';

const _L = Locale("ui-character");

const diffNames = [5156, 5155, 5154];
const questNames = [
  ['denofevil', 'qstsa1q1', () => _L`+${1} Skill Point`],
  ['radamentslair', 'qstsa2q1', () => _L`+${1} Skill Point`],
  ['thegoldenbird', 'qstsa3q4', () => _L`+${20} Maximum Life`],
  ['lamessenstome', 'qstsa3q1', () => _L`+${5} Attribute Points`],
  ['thefallenangel', 'qstsa4q1', () => _L`+${2} Skill Points`],
  ['prisonofice', 'qstsa5q3', () => _L`+${10} All Resistances`],
];

function QuestList() {
  const quests = Store.useProfile(profile => profile.quests);
  const dispatch = Store.useDispatch();
  const onCheck = React.useCallback((index, id, value) => dispatch.setProfile(profile => {
    const quests = profile.quests?.slice() || [];
    quests[index] = {...quests[index]};
    quests[index][id] = value;
    return profileUpdateAttributes({...profile, quests});
  }), [dispatch]);
  const onCheckAct = React.useCallback((index, value) => dispatch.setProfile(profile => {
    const quests = profile.quests?.slice() || [];
    quests[index] = {...quests[index]};
    for (const [id] of questNames) {
      quests[index][id] = value;
    }
    return profileUpdateAttributes({...profile, quests});
  }), [dispatch]);
  return (
    <div className="d2p-GroupBox">
      <span className="d2p-title"><span>{_L`Quests`}</span></span>
      <table className="d2p-QuestList">
        <thead>
          <tr>
            <th>Name</th>
            {[0, 1, 2].map(index => <th key={index}>{Locale.istrings[diffNames[index]]}</th>)}
          </tr>
        </thead>
        <tbody>
          {questNames.map(([id, name, bonus]) => (
            <tr key={id}>
              <td>
                <span>{Locale.strings[name]}</span>
                <span className={`d2-color-${Color.GRAY}`}>{bonus()}</span>
              </td>
              {[0, 1, 2].map(index => (
                <td key={index}>
                  <input type="checkbox" checked={!!quests?.[index]?.[id]} onChange={e => onCheck(index, id, !!e.target.checked)}/>
                </td>
              ))}
            </tr>
          ))}
          <tr>
            <td/>
            {[0, 1, 2].map(index => (
              <td key={index} className="d2p-act-toggle">
                <span onClick={() => onCheckAct(index, true)}>All</span>/
                <span onClick={() => onCheckAct(index, false)}>None</span>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const statNames = [
  ['str', 'strchrstr', 'strength', 'equipstr'],
  ['dex', 'strchrdex', 'dexterity', 'equipdex'],
  ['vit', 'strchrvit', 'vitality'],
  ['int', 'strchreng', 'energy'],
];

// function AttributeButtonSD({ icon, active, ...props }) {
//   const [pressed, setPressed] = React.useState(false);
//   const ref = React.useRef();
//   React.useEffect(() => {
//     const ctx = ref.current.getContext('2d');
//     const frame = icon?.frames?.[active ? (pressed ? 1 : 0) : 2];
//     if (frame) {
//       ctx.drawImage(icon.bitmap, frame.x, frame.y, frame.width, frame.height, 0, 0, frame.width, frame.height);
//     } else {
//       ctx.clearRect(0, 0, ref.current.width, ref.current.height);
//     }
//   }, [icon, ref, active, pressed]);
//   const handlers = {
//     onMouseDown: () => setPressed(true),
//     onMouseUp: () => setPressed(false),
//     ...props
//   };
//   return <canvas className="d2p-button" width={30} height={30} {...handlers} ref={ref}/>;
// }

// function AttributeButton({ hd, icon, active, ...props }) {
//   return hd ? <div className={classNames("d2p-button-hd", {"d2p-disabled": !active})} {...props}/> :
//     <AttributeButtonSD icon={icon} active={active} {...props}/>;
// }

function AttributesList() {
  // const [icon, setIcon] = React.useState();
  // const alive = React.useRef(true);
  // const gfx = React.useContext(GameContext).gfx;
  // React.useEffect(() => {
  //   if (!gfx.hd) {
  //     alive.current = true;
  //     getCommonImage('level').then(icon => alive.current && setIcon(icon));
  //     return () => alive.current = false;
  //   }
  // }, [gfx]);

  const finalStats = Store.useSelector(selectStats);
  const stats = Store.useProfile(profile => profile.stats);
  const maxPoints = Store.useProfile(profileMaxAttributes);
  const spentPoints = Object.values(stats).reduce((sum, value) => sum + value, 0);
  const dispatch = Store.useDispatch();
  const onChange = React.useCallback((stat, value) => dispatch.setProfile(profile => {
    const stats = {...profile.stats};
    const maxPoints = profileMaxAttributes(profile);
    const spentNow = Object.values(stats).reduce((sum, value) => sum + value, 0);
    const spentNext = spentNow - (stats[stat] || 0) + value;
    if (spentNext > maxPoints) {
      value -= (spentNext - maxPoints);
      if (value < (stats[stat] || 0)) return profile;
    }
    stats[stat] = value;
    return {...profile, stats};
  }), [dispatch]);

  return (
    <div className="d2p-GroupBox">
      <span className="d2p-title"><span>{_L`Attributes (${Math.max(0, maxPoints - spentPoints)} remaining)`}</span></span>
      <table className="d2p-AttributesList">
        <tbody>
          {statNames.map(([key, name, stat, req]) => (
            <tr key={key}>
              <td>{Locale.strings[name]} +</td>
              <td><NumberInput min={0} max={maxPoints - spentPoints + (stats[key] || 0)} value={stats[key] || 0} onChange={value => onChange(key, value)}/></td>
              <td className={req && finalStats.player[req] > finalStats.player.base[stat] ? `d2-color-${Color.RED}` : ''}>
                ({finalStats.player.base[stat]}{!!(finalStats.player[req] > finalStats.player.base[stat]) && ` / ${finalStats.player[req]}`})
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default React.memo(function CharacterPane() {
  return (
    <div className="d2p-CharacterPane">
      <SkillTree/>
      <div className="d2p-after-tree">
        <QuestList/>
        <AttributesList/>
      </div>
    </div>
  );
});
